import { compact } from '@art-suite/art-core-ts';
import { ApiOrg, ApiOrgProduct, CombinedProductSearchResponse, RetailerProduct } from '@kaiizen/kai-pro-api';

export type CombinedOrgProductRetailerProduct = Omit<ApiOrgProduct, 'id' | 'orgId' | 'createdAt' | 'updatedAt' | 'archivedAt'> & {
  id?: string;
  orgId?: string;
  createdAt?: Date;
  updatedAt?: Date;
  archivedAt?: Date;
};

export const getCombinedOrgProductFromCombinedSearchResponse = (response: CombinedProductSearchResponse): CombinedOrgProductRetailerProduct => ({
  id: response.id,
  orgId: response.orgId,
  sku: response.internetSkuId,
  thdStoreSku: response.storeSkuId,
  image: response.imageUrl,
  color: response.color,
  price: response.price,
  title: response.title,
  description: response.description,
  brand: response.brand,
  productSupplierOrgId: response.productSupplierOrgId,
  productSupplier: response.supplierName,
});

// TODO - RetailProduct should be the same structure as ApiOrgProduct, so we can just use ApiOrgProduct here
export const getCombinedOrgProductFromRetailerProduct = (retailerProduct: RetailerProduct): CombinedOrgProductRetailerProduct => {
  const combinedOrgProduct: CombinedOrgProductRetailerProduct = {
    sku: retailerProduct.internetSkuId,
    thdStoreSku: retailerProduct.storeSkuId,
    image: retailerProduct.imageUrl,
    color: retailerProduct.color,
    price: retailerProduct.price,
    title: retailerProduct.title,
    description: retailerProduct.description,
    brand: retailerProduct.brand,
    productSupplierOrgId: retailerProduct.productSupplierOrgId,
    productSupplier: retailerProduct.supplierName,
  };
  return combinedOrgProduct;
};

/** The supplier Org a new product from a catalog result is created under: the result's own, else Home Depot (results from before catalog V2 name none). */
export const getProductSupplierOrgIdForNewOrgProduct = (
  product: Pick<CombinedOrgProductRetailerProduct, 'productSupplierOrgId'>,
  homeDepotOrgId: string | undefined,
): string | undefined => product.productSupplierOrgId ?? homeDepotOrgId;

/** Where a search result comes from: the org's own products, else the result's supplier (Home Depot for results from before catalog V2, which name none). */
export const getProductSearchResultSource = (product: Pick<CombinedOrgProductRetailerProduct, 'orgId' | 'productSupplier'>): string =>
  product.orgId ? 'In your products' : product.productSupplier || 'Home Depot';

/** A search result's Supplier cell: its supplier beside its SKU. A catalog result always names one (Home Depot for results from before catalog V2); one of the org's own products names whatever supplier it was entered with. */
export const getProductSearchResultSupplierCell = (
  product: Pick<CombinedOrgProductRetailerProduct, 'orgId' | 'productSupplier' | 'thdStoreSku' | 'sku'>,
): string => compact([product.productSupplier || (product.orgId ? undefined : 'Home Depot'), product.thdStoreSku ?? product.sku]).join(' / ');

/**
 * Whether a product can be ordered from its supplier. The one place availability is decided, so
 * dedicated availability fields can replace today's inference without touching its callers.
 *
 * Today only Home Depot products carry availability, and it's inferred: one without a store SKU is
 * unavailable. Every other product counts as available.
 */
export const getOrgProductIsAvailable = (orgProduct: CombinedOrgProductRetailerProduct, homeDepotOrgId: string | undefined) =>
  !homeDepotOrgId || orgProduct.productSupplierOrgId !== homeDepotOrgId || !!orgProduct.thdStoreSku;

/** The product's own supplier text when it has one, else the name of the supplier Org it is linked to. `productSupplierOrgsById` is keyed by Org id. */
export const getOrgProductProductSupplierDisplayName = (orgProduct: CombinedOrgProductRetailerProduct, productSupplierOrgsById: Record<string, ApiOrg>) =>
  orgProduct.productSupplier
  || (orgProduct.productSupplierOrgId && productSupplierOrgsById?.[orgProduct.productSupplierOrgId]?.name)
  || '';

export const testOrgProductMatch = (
  searchRegex: RegExp | undefined | null,
  orgProduct: CombinedOrgProductRetailerProduct,
  productSupplierOrgs: Record<string, ApiOrg>,
) => {
  if (!searchRegex) return true;

  return (
    (orgProduct.sku && searchRegex.test(orgProduct.sku))
    || (orgProduct.thdStoreSku && searchRegex.test(orgProduct.thdStoreSku))
    || (orgProduct.title && searchRegex.test(orgProduct.title))
    || (orgProduct.description && searchRegex.test(orgProduct.description))
    || (orgProduct.brand && searchRegex.test(orgProduct.brand))
    || (orgProduct.color && searchRegex.test(orgProduct.color))
    || searchRegex.test(getOrgProductProductSupplierDisplayName(orgProduct, productSupplierOrgs))
  );
};
